<template>
  <div>
    <!-- Breadcrumbs -->
    <breadcrumb :items="breadcrumbs" />

    <v-container fluid class="goal-details-container">
      <v-card flat class="main-content-card pa-6 mb-6">
        <!-- Header -->
        <div class="d-flex justify-space-between align-center flex-wrap mb-6">
          <h2 class="page-title">{{ $t("esgPlatform.questions") }}</h2>
          <div class="d-flex" style="gap: 10px">
            <v-btn
              color="primary"
              class="text-uppercase font-weight-bold"
              @click="goBack"
            >
              {{ $t("back") }}
            </v-btn>
            <v-btn
              color="primary"
              dark
              class="text-uppercase font-weight-bold"
              @click="goToResponses"
            >
              {{ $t("esgPlatform.assessmentFarmer.showResponses") }}
            </v-btn>
          </div>
          
        </div>

        <!-- Info Area -->
        <div class="meta-area mb-8" v-if="responseData">
          <div class="meta-row pb-2">
            <span class="meta-label" style="min-width: 60px"
              >{{ $t("esgPlatform.goal") }}:</span
            >
            <span class="meta-value">{{ responseData.goaldata?.title }}</span>
          </div>
          <v-row dense>
            <!-- Row 1 -->
            <v-col cols="12" md="6">
              <div class="meta-row">
                <span class="meta-label" style="min-width: 160px"
                  >{{ $t("esgPlatform.progress.companyName") }}:</span
                >
                <span class="meta-value">{{
                  responseData.company?.name
                }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="meta-row pl-md-4">
                <span class="meta-label" style="min-width: 150px"
                  >{{ $t("esgPlatform.assessmentFarmer.total") }}:</span
                >
                <span class="meta-value">{{
                  responseData.goaldata?.noOfQuestions
                }}</span>
              </div>
            </v-col>

            <!-- Row 2 -->
            <v-col cols="12" md="6">
              <div class="meta-row align-center">
                <span class="meta-label" style="min-width: 160px"
                  >{{ $t("esgPlatform.progress.progress") }}:</span
                >
                <div
                  class="d-flex align-center flex-grow-1"
                  style="max-width: 180px"
                >
                  <v-progress-linear
                    :color="getProgressColor(progress)"
                    height="10"
                    :value="progress"
                    rounded
                    background-color="#E6E9EE"
                    class="mr-3"
                  />
                  <span class="progress-value">{{ progress }}%</span>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="meta-row pl-md-4">
                <span class="meta-label" style="min-width: 150px"
                  >{{ $t("esgPlatform.submittedBy") }}:</span
                >
                <span class="meta-value">{{
                  responseData.submittedBy.name
                }}</span>
              </div>
            </v-col>

            <!-- Row 3 -->
            <v-col cols="12" md="6">
              <div class="meta-row">
                <span class="meta-label" style="min-width: 160px"
                  >{{ $t("esgPlatform.assessmentFarmer.targetYear") }}:</span
                >
                <span class="meta-value">{{
                  responseData.goaldata?.targetYears
                }}</span>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Questions Table Area -->
        <div class="questions-section" v-if="responseData">
          <!-- Table Header -->
          <v-row dense align="center" class="questions-table-head ma-0 w-100">
            <v-col cols="12" md="8" class="pa-0">
              <div class="pl-4">{{ $t("esgPlatform.questions") }}</div>
            </v-col>
            <v-col cols="12" md="4" class="pa-0">
              <div>{{ $t("esgPlatform.type") }}</div>
            </v-col>
            <!-- <v-col cols="6" md="2" class="pa-0">
              <div>{{ $t('esgPlatform.responses') }}</div>
            </v-col> -->
          </v-row>

          <!-- Headings & Questions -->
          <div
            v-for="(heading, hIdx) in responseData.assessments"
            :key="heading._id"
            class="question-group mb-4"
          >
            <div v-if="heading.assessmentQuestions.length" class="group-header">
              <div class="group-header-left">
                <span class="group-index-badge">{{ hIdx + 1 }}</span>
                <span class="group-title">{{ heading.title }}</span>
              </div>
            </div>

            <div class="group-body">
              <v-row
                v-for="(question, qIdx) in heading.assessmentQuestions"
                :key="question._id"
                align="center"
                class="question-row w-100 ma-0"
              >
                <!-- Question -->
                <v-col
                  cols="12"
                  md="8"
                  class="pa-0 d-flex align-center pl-4"
                  style="gap: 16px; min-width: 0"
                >
                  <span class="question-number-tag tag-pending"
                    >{{ hIdx + 1 }}.{{ qIdx + 1 }}</span
                  >
                  <span class="question-title">{{ question.title }}</span>
                </v-col>

                <!-- Type -->
                <v-col
                  cols="12"
                  class="pa-0 pr-6 d-flex align-center justify-space-between"
                  md="4"
                >
                  <div class="type-value">
                    {{ formatQuestionType(question.questionType) }}
                  </div>
                  <v-btn
                    class="view-action-btn"
                    icon
                    x-small
                    @click="viewQuestionResponses(question)"
                  >
                    <v-icon size="18" color="#00BD73">mdi-eye-outline</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </v-card>

      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary" />
      </div>
    </v-container>
  </div>
</template>

<script>
import EsgService from "@/_services/EsgService";
import LoadingMixin from "@/mixins/LoadingMixin";

export default {
  mixins: [LoadingMixin],
  data() {
    return {
      assessmentId: this.$route.params.assessmentId,
      goalId: this.$route.params.goalId,
      submittedById: this.$route.params.submittedById,
      protocolId: this.$route.query.protocolId,
      progress: Number(this.$route.query.progress) || 0,
      responseData: null,
      loading: false,
      breadcrumbs: [
        {
          text: this.$t("esgPlatform.esgPlatform"),
          link: true,
          exact: true,
          disabled: true,
        },
        {
          text: this.$t("dashboard"),
          disabled: false,
          to: { name: "eudrDashboard" },
        },
      ],
    };
  },
  mounted() {
    this.fetchAllResponses();
  },
  methods: {
    async fetchAllResponses() {
      this.loading = true;
      this.startLoading();
      try {
        const { data } = await EsgService.getAllSurveyResponses(
          this.assessmentId,
          this.goalId,
          {
            submittedById: this.submittedById,
          },
        );
        this.responseData = data;
      } catch (error) {
        console.error("Error fetching survey responses:", error);
        this.$notify({ type: "error", text: this.$t("esgPlatform.assessmentFarmer.errorLoadingSurveyResponses") });
      } finally {
        this.loading = false;
        this.stopLoading();
      }
    },
    getProgressColor(value) {
      if (value <= 24) return "#F03737";
      if (value <= 74) return "#FFB443";
      return "#00BD73";
    },
    formatQuestionType(type) {
      if (!type) return "-";

      const questionTypeTranslations = {
        "text-field": "esgPlatform.textField",
        "text-area": "esgPlatform.textArea",
        numeric: "esgPlatform.textFieldNumeric",
        "radio-button": "esgPlatform.radioButton",
        "check-boxes": "esgPlatform.checkBoxes",
        "attach-files": "esgPlatform.attachFiles",
        "digital-signature": "esgPlatform.digitalSignature",
        "get-data": "esgPlatform.getDataLabel",
        date: "esgPlatform.date",
        "dimitra-module": "esgPlatform.validation.dimitraModule",
      };
      const translationKey = questionTypeTranslations[type];
      if (translationKey) {
        return this.$t(translationKey);
      }

      return String(type)
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    },
    goBack() {
      this.$router.go(-1);
    },
    goToResponses() {
      if (!this.assessmentId || !this.goalId || !this.submittedById) {
        this.$notify({
          type: "error",
          text: this.$t("esgPlatform.assessmentFarmer.missingRouteParams"),
        });
        return;
      }
      this.$router.push({
        name: "assessmentFarmerResponses",
        params: {
          assessmentId: this.assessmentId,
          goalId: this.goalId,
          submittedById: this.submittedById,
        },
      });
    },
    viewQuestionResponses(question) {
      if (!this.assessmentId || !this.goalId || !this.submittedById || !question?._id) {
        this.$notify({
          type: "error",
          text: this.$t("esgPlatform.assessmentFarmer.missingRouteParams"),
        });
        return;
      }

      this.$router.push({
        name: "assessmentFarmerResponses",
        params: {
          assessmentId: this.assessmentId,
          goalId: this.goalId,
          submittedById: this.submittedById,
        },
        query: {
          questionId: question._id,
          ...(this.protocolId && { protocolId: this.protocolId }),
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.goal-details-container {
  padding-bottom: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
}

.main-content-card {
  background: #ffffff;
  border: 1px solid #e4e8ef;
  border-radius: 12px;
}

.meta-row {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.meta-label {
  font-size: 16px;
  font-weight: 600;
  color: #000000;
}

.meta-value {
  font-size: 15px;
  color: #1d2939;
}

.progress-value {
  font-size: 16px;
  font-weight: 700;
  color: #1d2939;
  white-space: nowrap;
}

/* Header row for the table */
.questions-table-head {
  background: #1f7f4c;
  color: #ffffff;
  border-radius: 4px;
  padding: 14px 0;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 16px !important;
}

.question-group {
  overflow: visible;
}

.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #d5d5d5;
  border-radius: 6px;
  padding: 10px 16px;
}

.group-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.group-index-badge {
  min-width: 28px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: #787878;
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
}

.group-title {
  color: #3f3f46;
  font-size: 16px;
  font-weight: 900;
}

.group-body {
  padding-top: 8px;
}

/* Question body row */
.question-row {
  background: #ffffff;
  border-radius: 4px;
  margin-bottom: 8px !important;
  padding: 12px 0;
  padding-left: 12px;
}

.question-number-tag {
  min-width: 28px;
  height: 28px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: #acacac;
  font-size: 13px;
  font-weight: 700;
}

.tag-pending {
  background: #9b9da3;
}

.question-title {
  color: #03050a;
  font-weight: 500;
  font-size: 15px;
  line-height: 1.35;
}

.type-value {
  color: #2c3138;
  font-weight: 500;
  font-size: 15px;
}

.view-action-btn {
  width: 32px;
  height: 32px;
  border: 2px solid #75d9a9;
  border-radius: 50%;
}

.view-action-btn::before {
  opacity: 0;
}
</style>
